import { createSlice } from "@reduxjs/toolkit";
// Import all thunks from modular postThunks folder
import {
  createPost,
  fetchSinglePost,
  fetchUserPosts,
  fetchSavedPosts,
  fetchPostLikes,
  updatePost,
  removePost,
  togglePostLike,
  checkPostLike,
  savePostToLocal,
  unsavePostFromLocal,
  reportPost,
  setupUserLikesListener,
  setupPostsListener,
  hydratePostsFromCache,
} from "./postThunks/index.js";


const initialState = {
  posts: [],
  userPosts: [],
  savedPosts: [],
  loading: false,
  error: null,
  reportError: null,
  postLikeStatus: {}, // { postId: { isLiked: boolean, likes: number } }
  /** Timestamp of last optimistic like toggle per post — stale checkPostLike results are ignored */
  lastLikeToggleAt: {},
  /** Timestamp of last save/unsave mutation — stale fetchSavedPosts results merge instead of replace */
  lastSavedPostsMutationAt: 0,
  /** Pending deltas until Firestore snapshot catches up (footer + pagination) */
  pendingCommentCountDelta: {},
  pendingTopLevelCommentDelta: {},
  listener: null,
  likesListener: null,
};

const postSlice = createSlice({
  name: "posts",
  initialState,
  reducers: {
    adjustPendingCommentDeltas: (state, action) => {
      const {
        postId,
        commentCount = 0,
        topLevelCommentCount = 0,
      } = action.payload || {};
      if (!postId) return;
      state.pendingCommentCountDelta[postId] =
        (state.pendingCommentCountDelta[postId] || 0) + commentCount;
      state.pendingTopLevelCommentDelta[postId] =
        (state.pendingTopLevelCommentDelta[postId] || 0) + topLevelCommentCount;
    },
    setPosts: (state, action) => {
      // Preserve individually-fetched posts (e.g., priority post from reshare click)
      // that aren't in the listener snapshot (which is limited to 50 newest posts).
      // Exclude optimistic posts — they are replaced by the listener snapshot when it arrives.
      const incomingIds = new Set(action.payload.map((p) => p.id));
      const preservedPosts = state.posts.filter(
        (p) =>
          p.__fetchedIndividually &&
          !incomingIds.has(p.id) &&
          !p.id?.startsWith("temp-") &&
          !p.__optimistic,
      );

      const mappedPosts = action.payload.map((post) => {
        const cDelta = state.pendingCommentCountDelta[post.id] || 0;
        const tlDelta = state.pendingTopLevelCommentDelta[post.id] || 0;
        const baseCount = post.commentCount || 0;
        const baseTop = (post.topLevelCommentCount ?? post.commentCount) || 0;
        return {
          ...post,
          commentCount: Math.max(0, baseCount + cDelta),
          topLevelCommentCount: Math.max(0, baseTop + tlDelta),
          shares: post.shares || 0,
        };
      });

      // Append preserved posts (applySeenDemotion will reorder correctly via priorityPostId)
      state.posts =
        preservedPosts.length > 0
          ? [...mappedPosts, ...preservedPosts]
          : mappedPosts;

      try {
        state.posts.forEach((p) => {
          const existing = state.postLikeStatus[p.id];
          const likes =
            typeof p.likeCount === "number"
              ? p.likeCount
              : existing?.likes || 0;
          state.postLikeStatus[p.id] = {
            isLiked: existing?.isLiked || false,
            likes,
          };
        });
      } catch {}
    },
    clearError: (state) => {
      state.error = null;
    },
    updatePostLikes: (state, action) => {
      const { postId, isLiked, count } = action.payload;
      if (!state.postLikeStatus[postId]) {
        state.postLikeStatus[postId] = { isLiked: false, likes: 0 };
      }

      if (isLiked !== undefined) {
        state.postLikeStatus[postId].isLiked = isLiked;
      }

      if (count !== undefined) {
        state.postLikeStatus[postId].likes = count;
      }
    },
    updatePostCommentCount: (state, action) => {
      const {
        postId,
        newCommentCount,
        increment: incrementBy,
      } = action.payload;
      const postIndex = state.posts.findIndex((post) => post.id === postId);
      if (postIndex !== -1) {
        if (incrementBy !== undefined) {
          // Increment/decrement mode (for multi-level counters)
          state.posts[postIndex].commentCount =
            (state.posts[postIndex].commentCount || 0) + incrementBy;
        } else if (newCommentCount !== undefined && newCommentCount !== null) {
          // Direct set mode
          state.posts[postIndex].commentCount = newCommentCount;
        }
      }
    },
    incrementPostCommentCount: (state, action) => {
      const { postId, delta = 1 } = action.payload;
      const postIndex = state.posts.findIndex((post) => post.id === postId);
      if (postIndex !== -1) {
        state.posts[postIndex].commentCount = Math.max(0,
          (state.posts[postIndex].commentCount || 0) + delta);
      }
    },
    addToSavedPosts: (state, action) => {
      const post = action.payload;
      if (!state.savedPosts.some((p) => p.id === post.id)) {
        state.savedPosts.push(post);
      }
    },
    removeFromSavedPosts: (state, action) => {
      const { postId } = action.payload;
      state.savedPosts = state.savedPosts.filter((post) => post.id !== postId);
    },
    cleanupPostsListener: (state) => {
      if (state.listener) {
        try {
          state.listener();
        } catch {}
        state.listener = null;
      }
    },
    clearPostState: (state) => {
      state.postLikeStatus = {};
      state.savedPosts = [];
      state.error = null;
      state.lastLikeToggleAt = {};
      state.lastSavedPostsMutationAt = 0;
      state.pendingCommentCountDelta = {};
      state.pendingTopLevelCommentDelta = {};
    },
    addOptimisticPost: (state, action) => {
      // Add a temporary post to the top of the feed for immediate UI feedback.
      // Marked with __optimistic so it can be replaced by the real document later.
      const tempPost = { ...action.payload, __optimistic: true };
      // Remove any existing optimistic post with the same temp ID
      state.posts = state.posts.filter(p => p.id !== tempPost.id);
      state.posts.unshift(tempPost);
    },
    removeOptimisticPost: (state, action) => {
      // Remove optimistic post on failure
      state.posts = state.posts.filter(p => p.id !== action.payload);
    },
    incrementLocalShareCount: (state, action) => {
      // Optimistically increment share count before Firestore round-trip completes.
      const { postId } = action.payload;
      const post = state.posts.find(p => p.id === postId);
      if (post) {
        post.shares = (post.shares || 0) + 1;
      }
    },
    setPostIsLiked: (state, action) => {
      const { postId, isLiked } = action.payload || {};
      if (!postId) return;
      const existing = state.postLikeStatus[postId] || {
        isLiked: false,
        likes: 0,
      };
      state.postLikeStatus[postId] = { ...existing, isLiked: !!isLiked };
    },
    cleanupUserLikesListener: (state) => {
      if (state.likesListener) {
        try {
          state.likesListener();
        } catch {}
        state.likesListener = null;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch user posts
      .addCase(fetchUserPosts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserPosts.fulfilled, (state, action) => {
        state.loading = false;
        state.userPosts = action.payload.map((post) => {
          const cDelta = state.pendingCommentCountDelta[post.id] || 0;
          const tlDelta = state.pendingTopLevelCommentDelta[post.id] || 0;
          const baseCount = post.commentCount || 0;
          const baseTop = (post.topLevelCommentCount ?? post.commentCount) || 0;
          return {
            ...post,
            commentCount: Math.max(0, baseCount + cDelta),
            topLevelCommentCount: Math.max(0, baseTop + tlDelta),
            shares: post.shares || 0,
          };
        });
      })
      .addCase(fetchUserPosts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create post
      .addCase(createPost.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        // Use requestId to generate a deterministic temp id per request
        const tempId = `temp-${action.meta.requestId}`;
        const newPost = {
          ...action.meta.arg,
          id: tempId,
          __tempRequestId: action.meta.requestId,
          commentCount: 0,
          topLevelCommentCount: 0,
          shares: 0,
        };
        state.posts = [newPost, ...state.posts];
      })
      .addCase(createPost.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        const tempId = `temp-${action.meta.requestId}`;
        // Replace only the matching temp post with the real one from the server
        state.posts = state.posts.map((post) => {
          if (
            post.id === tempId ||
            post.__tempRequestId === action.meta.requestId
          ) {
            return { ...action.payload, commentCount: 0, shares: 0 };
          }
          return post;
        });
        // If no temp found (e.g., listener replaced it), ensure it's present in userPosts
        state.userPosts = [
          { ...action.payload, commentCount: 0, shares: 0 },
          ...state.userPosts,
        ];
      })
      .addCase(createPost.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        const tempId = `temp-${action.meta.requestId}`;
        // Remove the temp post corresponding to this failed request
        state.posts = state.posts.filter(
          (p) => p.id !== tempId && p.__tempRequestId !== action.meta.requestId,
        );
      })
      // Delete post
      .addCase(removePost.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removePost.fulfilled, (state, action) => {
        state.loading = false;
        state.userPosts = state.userPosts.filter(
          (post) => post.id !== action.payload,
        );
        state.posts = state.posts.filter((post) => post.id !== action.payload);
        // Also remove from savedPosts if present
        state.savedPosts = state.savedPosts.filter(
          (post) => post.id !== action.payload,
        );
      })
      .addCase(removePost.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Toggle share post (optimistic)
      // Update post
      .addCase(updatePost.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updatePost.fulfilled, (state, action) => {
        state.loading = false;
        const updated = action.payload;
        state.posts = state.posts.map((p) =>
          p.id === updated.id ? { ...p, ...updated } : p,
        );
        state.userPosts = state.userPosts.map((p) =>
          p.id === updated.id ? { ...p, ...updated } : p,
        );
      })
      .addCase(updatePost.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Toggle like post (fast path: don't toggle global loading)
      .addCase(togglePostLike.pending, (state, action) => {
        state.error = null;
        try {
          const { postId } = action.meta.arg || {};
          if (!postId) return;
          state.lastLikeToggleAt[postId] = Date.now();
          const prev = state.postLikeStatus[postId] || {
            isLiked: false,
            likes: 0,
          };
          const nextLiked = !prev.isLiked;
          const nextLikes = nextLiked
            ? (prev.likes || 0) + 1
            : Math.max(0, (prev.likes || 0) - 1);
          state.postLikeStatus[postId] = {
            isLiked: nextLiked,
            likes: nextLikes,
          };
        } catch (_) {}
      })
      .addCase(togglePostLike.fulfilled, (state, action) => {
        const { postId, isLiked, likes } = action.payload;
        state.postLikeStatus[postId] = { isLiked, likes };
      })
      .addCase(togglePostLike.rejected, (state, action) => {
        try {
          const { postId } = action.meta.arg || {};
          if (postId) {
            const cur = state.postLikeStatus[postId] || {
              isLiked: false,
              likes: 0,
            };
            const revertLiked = !cur.isLiked;
            const revertLikes = revertLiked
              ? (cur.likes || 0) + 1
              : Math.max(0, (cur.likes || 0) - 1);
            state.postLikeStatus[postId] = {
              isLiked: revertLiked,
              likes: revertLikes,
            };
          }
        } catch (_) {}
        state.error = action.payload;
      })
      // Check post like
      .addCase(checkPostLike.fulfilled, (state, action) => {
        const { postId, isLiked, checkStartedAt } = action.payload;

        if (!state.postLikeStatus[postId]) {
          state.postLikeStatus[postId] = { isLiked: false, likes: 0 };
        }

        const lastToggle = state.lastLikeToggleAt[postId] || 0;
        if (typeof checkStartedAt === "number" && lastToggle > checkStartedAt) {
          return;
        }

        state.postLikeStatus[postId].isLiked = isLiked;
      })
      // Fetch post likes
      .addCase(fetchPostLikes.fulfilled, (state, action) => {
        const { postId, likes } = action.payload;

        if (!state.postLikeStatus[postId]) {
          state.postLikeStatus[postId] = { isLiked: false, likes: 0 };
        }

        state.postLikeStatus[postId].likes = likes;
      })
      // Save post locally
      .addCase(savePostToLocal.pending, (state) => {
        // Do not toggle global loading to avoid flicker in saved posts list
        state.error = null;
        state.lastSavedPostsMutationAt = Date.now();
      })
      .addCase(savePostToLocal.fulfilled, (state, action) => {
        // no-op: optimistic add already performed in reducer
      })
      .addCase(savePostToLocal.rejected, (state, action) => {
        state.error = action.payload;
      })
      // Unsave post locally
      .addCase(unsavePostFromLocal.pending, (state) => {
        // Do not toggle global loading to avoid flicker in saved posts list
        state.error = null;
        state.lastSavedPostsMutationAt = Date.now();
      })
      .addCase(unsavePostFromLocal.fulfilled, (state, action) => {
        // no-op: optimistic remove already performed in reducer
      })
      .addCase(unsavePostFromLocal.rejected, (state, action) => {
        state.error = action.payload;
      })
      // Fetch Saved Posts
      .addCase(fetchSavedPosts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSavedPosts.fulfilled, (state, action) => {
        state.loading = false;
        const { posts, fetchStartedAt } = action.payload;
        const serverList = Array.isArray(posts) ? posts : [];
        if (
          typeof fetchStartedAt === "number" &&
          (state.lastSavedPostsMutationAt || 0) > fetchStartedAt
        ) {
          const serverIds = new Set(serverList.map((p) => p.id));
          const extras = state.savedPosts.filter((p) => !serverIds.has(p.id));
          state.savedPosts = [...serverList, ...extras];
        } else {
          state.savedPosts = serverList;
        }
      })
      .addCase(fetchSavedPosts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Hydrate posts from local cache for instant first paint
      .addCase(hydratePostsFromCache.fulfilled, (state, action) => {
        if (Array.isArray(action.payload) && action.payload.length > 0) {
          state.posts = action.payload.map((post) => {
            const cDelta = state.pendingCommentCountDelta[post.id] || 0;
            const tlDelta = state.pendingTopLevelCommentDelta[post.id] || 0;
            const baseCount = post.commentCount || 0;
            const baseTop =
              (post.topLevelCommentCount ?? post.commentCount) || 0;
            return {
              ...post,
              commentCount: Math.max(0, baseCount + cDelta),
              topLevelCommentCount: Math.max(0, baseTop + tlDelta),
              shares: post.shares || 0,
            };
          });
          try {
            state.posts.forEach((p) => {
              const existing = state.postLikeStatus[p.id];
              const likes =
                typeof p.likeCount === "number"
                  ? p.likeCount
                  : existing?.likes || 0;
              state.postLikeStatus[p.id] = {
                isLiked: existing?.isLiked || false,
                likes,
              };
            });
          } catch {}
        }
      })
      .addCase(setupPostsListener.pending, (state) => {
        // Cleanup any existing listener
        if (state.listener) {
          try {
            state.listener();
          } catch {}
          state.listener = null;
        }
        state.loading = true;
      })
      .addCase(setupPostsListener.fulfilled, (state, action) => {
        state.listener = action.payload;
        state.loading = false;
      })
      .addCase(setupPostsListener.rejected, (state, action) => {
        state.error = action.payload;
        state.loading = false;
      })
      .addCase(setupUserLikesListener.rejected, (state, action) => {
        state.error = action.payload;
      })
      // Fetch single post (for deep links / priority posts from reshare clicks)
      .addCase(fetchSinglePost.fulfilled, (state, action) => {
        const post = action.payload;
        if (!state.posts.some((p) => p.id === post.id)) {
          const cDelta = state.pendingCommentCountDelta[post.id] || 0;
          const tlDelta = state.pendingTopLevelCommentDelta[post.id] || 0;
          const baseCount = post.commentCount || 0;
          const baseTop = (post.topLevelCommentCount ?? post.commentCount) || 0;
          // Add to start of posts list, marked so setPosts won't discard it
          state.posts = [
            {
              ...post,
              commentCount: Math.max(0, baseCount + cDelta),
              topLevelCommentCount: Math.max(0, baseTop + tlDelta),
              shares: post.shares || 0,
              __fetchedIndividually: true,
            },
            ...state.posts,
          ];

          // Initialize like status for this post
          const existingStatus = state.postLikeStatus[post.id];
          state.postLikeStatus[post.id] = {
            isLiked: existingStatus?.isLiked || false,
            likes: post.likeCount || 0,
          };
        }
      })
      // Report post
      .addCase(reportPost.pending, (state) => {
        state.reportError = null;
      })
      .addCase(reportPost.fulfilled, (state, action) => {
        // no global loading toggle to avoid feed flicker
      })
      .addCase(reportPost.rejected, (state, action) => {
        state.reportError = action.payload;
      });
  },
});

export const {
  setPosts,
  clearError,
  updatePostLikes,
  updatePostCommentCount,
  incrementPostCommentCount,
  adjustPendingCommentDeltas,
  addToSavedPosts,
  removeFromSavedPosts,
  cleanupPostsListener,
  clearPostState,
  setPostIsLiked,
  cleanupUserLikesListener,
  addOptimisticPost,
  removeOptimisticPost,
  incrementLocalShareCount,
} = postSlice.actions;

// Thunk to refresh the posts feed (cleanup and re-setup listener)
export const refreshPostsListener = () => (dispatch, getState) => {
  // First cleanup existing listener
  dispatch(cleanupPostsListener());
  // Clear cache to force fresh data
  try {
    localStorage.removeItem("feed_posts_cache");
  } catch {}
  // Re-setup listener
  return dispatch(setupPostsListener());
};

// ─── Comment Pagination Selectors (L-01) ──────────────────────────────────────
// Centralized pagination logic — components should READ these values, not calculate them.

/**
 * Returns pagination data for a specific post's comments.
 * @param {object} state - Redux state
 * @param {string} postId - The post ID
 * @returns {{ totalComments: number, loadedComments: number, remainingComments: number, hasMoreOnServer: boolean }}
 */
export const selectCommentPaginationData = (state, postId) => {
  const post = (state.posts.posts || []).find((p) => p.id === postId);
  // Use topLevelCommentCount for pagination (top-level comments only)
  // Fall back to commentCount for backward compatibility with old posts
  const totalComments = post?.topLevelCommentCount ?? post?.commentCount ?? -1;
  const loadedComments = (state.comments.items?.[postId] || []).length;
  const displayCount = state.comments.displayCount?.[postId] || 8;

  return {
    totalComments: totalComments >= 0 ? totalComments : loadedComments,
    loadedComments,
    displayCount,
    remainingComments: Math.max(
      0,
      (totalComments >= 0 ? totalComments : loadedComments) - displayCount,
    ),
    hasMoreOnServer:
      totalComments >= 0 ? totalComments > loadedComments : false,
  };
};

// Re-export all thunks for backward compatibility
export {
  createPost,
  fetchSinglePost,
  fetchUserPosts,
  fetchSavedPosts,
  fetchPostLikes,
  updatePost,
  removePost,
  togglePostLike,
  checkPostLike,
  savePostToLocal,
  unsavePostFromLocal,
  reportPost,
  setupUserLikesListener,
  setupPostsListener,
  hydratePostsFromCache,
};

export default postSlice.reducer;
